/**
 * Agent Chat — generic AI transport layer.
 *
 * Thin wrapper around SeeMe's AiChat UI shell.
 * Handles: WebSocket connection, session management, message routing.
 * UI (FAB, panel, messages, chips, working state) is all SeeMe.
 *
 * Scope-specific config (chips, overflow, help) comes from the DOM:
 *   <script type="application/json" id="agent-chat-config">{ ... }</script>
 *   <template id="agent-chat-help">...</template>
 *
 * See templates/agent/_init_deal.html for the pattern.
 *
 * Usage: AgentChat.init()  — reads config from #agent-chat-config
 */
var AgentChat = (function() {
    'use strict';

    var ws = null;
    var sessionId = null;
    var scopeType = null;
    var scopeId = null;
    var reconnectAttempts = 0;
    var MAX_RECONNECT = 5;
    var reconnectTimer = null;

    // SPA cleanup — remove FAB/panel and close WS when navigating away
    document.addEventListener('htmx:beforeSwap', function(e) {
        if (e.detail.target && e.detail.target.id === 'page-content') {
            destroy();
        }
    });

    function destroy() {
        reconnectAttempts = MAX_RECONNECT; // suppress reconnect
        clearTimeout(reconnectTimer);
        if (ws && ws.readyState <= WebSocket.OPEN) ws.close();
        ws = null;
        sessionId = null;
        scopeType = null;
        scopeId = null;
        reconnectAttempts = 0;

        // Remove body-appended DOM
        var fab = document.querySelector('.ai-chat-fab');
        var panel = document.getElementById('ai-chat-panel');
        if (fab) fab.remove();
        if (panel) panel.remove();
    }

    function init() {
        destroy(); // Clean up any previous instance

        // Read config from DOM (rendered by scope-specific template)
        var configEl = document.getElementById('agent-chat-config');
        if (!configEl) return;
        var config;
        try { config = JSON.parse(configEl.textContent); }
        catch (e) { console.error('AgentChat: invalid config JSON', e); return; }

        scopeType = config.scopeType;
        scopeId = config.scopeId;

        // Init the SeeMe UI shell with scope-specific chips
        AiChat.init({
            title: config.title || 'AI Assistant',
            placeholder: config.placeholder || 'Type an action\u2026',
            chips: config.chips || [],
        });

        // Inject overflow menu (⋯) into chips area — always render (Help is always present)
        var chipsEl = document.getElementById('ai-chat-chips');
        if (chipsEl) {
            var overflow = document.createElement('div');
            overflow.className = 'dropdown';

            var menuItems = '';
            var overflowItems = config.overflow || [];
            for (var i = 0; i < overflowItems.length; i++) {
                var item = overflowItems[i];
                menuItems +=
                    '<a class="dropdown-item" href="#" data-preset="' + escapeAttr(item.preset || '') + '">' +
                    (item.icon ? '<i class="' + item.icon + '"></i> ' : '') +
                    escapeHtml(item.label) + '</a>';
            }
            // Help is always last
            menuItems += '<a class="dropdown-item" href="#" data-action="help"><i class="fa-solid fa-circle-question"></i> Help</a>';

            overflow.innerHTML =
                '<button type="button" class="chip chip-sm chip-muted" data-dropdown>' +
                '  <i class="chip-icon fa-solid fa-ellipsis"></i>' +
                '</button>' +
                '<div class="dropdown-menu dropdown-menu--sm dropdown-menu-popout">' +
                menuItems +
                '</div>';
            chipsEl.appendChild(overflow);

            // Attach click handler to the menu itself, not `overflow`. SeeMe portals
            // popout menus to document.body when opened, so clicks inside the menu
            // don't bubble to `overflow` — the listener must travel with the menu.
            var overflowMenu = overflow.querySelector('.dropdown-menu');
            overflowMenu.addEventListener('click', function(e) {
                var el = e.target.closest('[data-action], [data-preset]');
                if (!el) return;
                e.preventDefault();
                if (el.getAttribute('data-action') === 'help') {
                    showHelp();
                } else {
                    var preset = el.getAttribute('data-preset');
                    if (preset) {
                        var input = document.getElementById('ai-chat-input');
                        if (input) {
                            input.value = preset;
                            input.focus();
                        }
                    }
                }
            });
        }

        // Delegated cancel handler for form cards inserted via innerHTML (scripts don't execute)
        var messagesEl = document.getElementById('ai-chat-messages');
        if (messagesEl) {
            messagesEl.addEventListener('click', function(e) {
                var btn = e.target.closest('.js-cancel-form-card');
                if (btn) btn.closest('.ai-chat__form-card').remove();
            });
        }

        // Hook up transport — SeeMe calls this when user sends a message
        AiChat.onSend(function(text) {
            sendMessage(text);
        });

        // Inject reset-chat dropdown into the input row.
        // Dropdown (rather than direct click) so clicking away is a clean no-op —
        // protects against accidental resets.
        var form = document.getElementById('ai-chat-form');
        if (form) {
            var modeDropdown = document.createElement('div');
            modeDropdown.className = 'dropdown dropup';
            modeDropdown.innerHTML =
                '<button class="ai-chat__mode" type="button" data-dropdown title="Reset chat">' +
                '  <i class="fa-solid fa-rotate-right"></i>' +
                '</button>' +
                '<div class="dropdown-menu dropdown-menu--sm">' +
                '  <a class="dropdown-item" href="#" data-action="reset-chat"><i class="fa-solid fa-rotate-right"></i> Reset Chat</a>' +
                '</div>';
            form.insertBefore(modeDropdown, form.firstChild);

            modeDropdown.addEventListener('click', function(e) {
                var el = e.target.closest('[data-action="reset-chat"]');
                if (!el) return;
                e.preventDefault();
                resetChat();
            });
        }

        // Load panel content when first opened.
        // SeeMe binds the local `open` fn directly to the FAB click — monkey-patching
        // AiChat.open doesn't intercept it. So we replace the FAB's listener.
        var fab = document.querySelector('.ai-chat-fab');
        if (fab) {
            // Remove SeeMe's handler by cloning the node (drops all listeners)
            var newFab = fab.cloneNode(true);
            fab.parentNode.replaceChild(newFab, fab);
            newFab.addEventListener('click', function() {
                AiChat.open();
                if (!sessionId) {
                    loadPanel();
                }
            });
        }
    }

    // ── Session management ──

    function loadPanel(forceNew) {
        AiChat.setWorking(true);
        var url = '/api/agent/panel/?scope_type=' + encodeURIComponent(scopeType) + '&scope_id=' + encodeURIComponent(scopeId);
        if (forceNew) url += '&new=1';
        fetch(url, { credentials: 'same-origin' })
        .then(function(r) {
            if (!r.ok) throw new Error('HTTP ' + r.status);
            return r.text();
        })
        .then(function(html) {
            var container = document.getElementById('ai-chat-messages');
            container.innerHTML = html;

            // Extract session ID from server-rendered wrapper
            var wrapper = container.querySelector('[data-session-id]');
            if (wrapper) {
                sessionId = parseInt(wrapper.getAttribute('data-session-id'), 10);
                // Unwrap — move children up so the wrapper div doesn't add nesting
                while (wrapper.firstChild) {
                    container.insertBefore(wrapper.firstChild, wrapper);
                }
                wrapper.remove();
            }

            AiChat.setWorking(false);
            if (sessionId) {
                connectWebSocket();
            }

            // Scroll to bottom if there's history
            container.scrollTop = container.scrollHeight;
        })
        .catch(function(err) {
            console.error('Load panel error:', err);
            AiChat.setWorking(false);
            AiChat.addMessage('system', 'Failed to load chat. Please try again.');
        });
    }

    function resetChat() {
        reconnectAttempts = MAX_RECONNECT; // suppress old socket's onclose reconnect
        clearTimeout(reconnectTimer);
        if (ws && ws.readyState <= WebSocket.OPEN) ws.close();
        ws = null;
        reconnectAttempts = 0; // allow reconnect for new session
        sessionId = null;
        loadPanel(true);
    }

    // ── WebSocket ──

    function connectWebSocket() {
        clearTimeout(reconnectTimer);
        if (ws && ws.readyState <= WebSocket.OPEN) {
            ws.close();
        }

        var protocol = window.location.protocol === 'https:' ? 'wss:' : 'ws:';
        ws = new WebSocket(protocol + '//' + window.location.host + '/ws/agent-chat/' + sessionId + '/');

        ws.onopen = function() {
            reconnectAttempts = 0;
            AiChat.setWorking(false);
        };

        ws.onmessage = function(e) {
            var data = JSON.parse(e.data);
            switch (data.type) {
                case 'session_init':
                    // History already server-rendered — just sync working state
                    AiChat.setWorking(data.status === 'working');
                    break;
                case 'message':
                    handleMessage(data.message);
                    break;
                case 'status':
                    AiChat.setWorking(data.status === 'working');
                    break;
                case 'error':
                    AiChat.addMessage('system', escapeHtml(data.message));
                    AiChat.setWorking(false);
                    break;
            }
        };

        ws.onclose = function() {
            var closedWs = this;
            // Only reconnect if this socket is still the active one
            if (closedWs !== ws) return;
            if (reconnectAttempts < MAX_RECONNECT) {
                var delay = Math.min(2000 * Math.pow(1.5, reconnectAttempts), 15000);
                reconnectAttempts++;
                reconnectTimer = setTimeout(connectWebSocket, delay);
            }
        };

        ws.onerror = function() {};
    }

    // ── Messaging ──

    function sendMessage(text) {
        if (!ws || ws.readyState !== WebSocket.OPEN) {
            AiChat.addMessage('system', 'Not connected. Reconnecting\u2026');
            if (sessionId) { connectWebSocket(); } else { loadPanel(); }
            return;
        }
        ws.send(JSON.stringify({ type: 'message', text: text }));
        AiChat.setWorking(true);
    }

    function handleMessage(msg) {
        var role = msg.role === 'assistant' ? 'ai' : msg.role;
        var html = role === 'ai' ? renderMarkdown(msg.content || '') : escapeHtml(msg.content || '');
        var el = AiChat.addMessage(role, html);
        var lastInserted = el;

        // Append tool-call pills for assistant messages
        if (msg.role === 'assistant' && msg.tool_calls && msg.tool_calls.length > 0) {
            var tools = document.createElement('div');
            tools.className = 'ai-chat__tools';
            for (var i = 0; i < msg.tool_calls.length; i++) {
                var tc = msg.tool_calls[i];
                var pill = document.createElement('span');
                pill.className = 'ai-chat__tool-pill';
                pill.textContent = formatToolName(tc.name);
                if (tc.result && tc.result.success) {
                    pill.classList.add('ai-chat__tool-pill--success');
                } else if (tc.result && tc.result.error) {
                    pill.classList.add('ai-chat__tool-pill--error');
                }
                tools.appendChild(pill);
            }
            el.parentNode.insertBefore(tools, lastInserted.nextSibling);
            lastInserted = tools;
        }

        // Render inline form card (HTMX partial from tool handler)
        if (msg.form_card) {
            var wrapper = document.createElement('div');
            wrapper.innerHTML = msg.form_card.trim();
            var formEl = wrapper.firstElementChild;
            if (formEl) {
                el.parentNode.insertBefore(formEl, lastInserted.nextSibling);
                if (window.htmx) htmx.process(formEl);
            }
        }
    }

    // ── Helpers ──

    function formatToolName(name) {
        return name.replace(/_/g, ' ').replace(/\b\w/g, function(c) { return c.toUpperCase(); });
    }

    function renderMarkdown(str) {
        var html = escapeHtml(str);
        html = html.replace(/```(\w*)\n([\s\S]*?)```/g, '<pre><code>$2</code></pre>');
        html = html.replace(/`([^`]+)`/g, '<code>$1</code>');
        html = html.replace(/\*\*(.+?)\*\*/g, '<strong>$1</strong>');
        html = html.replace(/\*(.+?)\*/g, '<em>$1</em>');
        html = html.replace(/^[*\-] (.+)$/gm, '<li>$1</li>');
        html = html.replace(/((?:<li>.*<\/li>\n?)+)/g, '<ul>$1</ul>');
        html = html.replace(/\n/g, '<br>');
        html = html.replace(/<pre><code>([\s\S]*?)<\/code><\/pre>/g, function(m, code) {
            return '<pre><code>' + code.replace(/<br>/g, '\n') + '</code></pre>';
        });
        return html;
    }

    function showHelp() {
        var tpl = document.getElementById('agent-chat-help');
        var html = tpl ? tpl.innerHTML : '<strong>Ask me anything.</strong>';
        AiChat.addMessage('system', html);
    }

    function escapeHtml(str) {
        var d = document.createElement('div');
        d.textContent = str;
        return d.innerHTML;
    }

    function escapeAttr(str) {
        return str.replace(/&/g, '&amp;').replace(/"/g, '&quot;').replace(/</g, '&lt;').replace(/>/g, '&gt;');
    }

    return { init: init, destroy: destroy };
})();

window.AgentChat = AgentChat;
